const { chunk, last, sumBy, mergeWith, unzip, zip, keyBy, sortBy, groupBy, fromPairs, } = require('lodash');
const numeral = require('numeral');

const { numberFormat, integerFormat, floatFormat, isFiscalYearFirstMonth } = require('../util');
const { accountItemCategories, accountItemCategoryNames } = require('../config');

const { keys, entries, } = Object;
const { abs } = Math;
const accountItemCategoriesByName = keyBy(accountItemCategories, 'name');
const accountItemCategoriesGroupedByType = groupBy(accountItemCategories, 'type');
const aggregationTypes = {
  individual: {
    label: '個別',
    amountName: 'closingBalance',
  },
  individualAdjustment: {
    label: '個別修正',
    amountName: 'adjustmentAmount',
  },
  individualAdjusted: {
    label: '換算後',
    amountName: 'exchangedAmount',
  },
  consolidation: {
    label: '連結仕訳',
    amountName: 'consolidationAmount',
  },
  consolidated: {
    label: '連結消去後',
    amountName: 'consolidatedAmount',
  },
};
const formatByAggregationType = _ => ['individual', 'individualAdjustment'].includes(_) ? numberFormat : (_, v) => integerFormat(v);
const metrics = {
  current: {
    label: _ => '当期',
    format: (v, a, r) => formatByAggregationType(a)(r?.currency, v),
    compute: (amount) => {
      return amount;
    },
  },
  prev: {
    label: _ => comparisons[_.comparison]?.label,
    format: (v, a, r) => formatByAggregationType(a)(r?.currency, v),
    compute: (amount, comparisonAmount) => {
      return comparisonAmount;
    },
  },
  change: {
    label: _ => '増減額',
    format: (v, a, r) => formatByAggregationType(a)(r?.currency, v),
    compute: (amount, comparisonAmount) => {
      return amount - comparisonAmount;
    },
  },
  changeRate: {
    label: _ => '増減率',
    format: _ => numeral(_).format('0,0.0%'),
    compute: (amount, comparisonAmount) => {
      return metrics.change.compute(amount, comparisonAmount) / comparisonAmount;
    },
  },
};

const generateQuarters = (documentType, comparison, allMonthColumns, compute) => {
  const allQuarters = chunk(allMonthColumns, 3).map((monthColumns, quarterIndex) => {
    const [{ isCurrent, isCurrentFirst, }] = monthColumns;
    const quarterColumn = {
      amount: _ => {
        // NOTE: BSは月末残高(累計)なので四半期末の値、PL/CRは各月の単月(displayAmount)の合計。
        // displayAmountは月次で既に単月化済み(決算期境界でも正しい)なので、合計すれば四半期の単月になる。
        return documentType === 'bs' ? last(monthColumns)?.amount : sumBy(monthColumns, _ => _.displayAmount);
      },
      comparisonAmount: _ => {
        return ({
          prevYear: _ => {
            return documentType === 'bs' ? last(monthColumns)?.comparisonAmount : sumBy(monthColumns, _ => _.comparisonDisplayAmount);
          },
          prevMonth: _ => allQuarters[quarterIndex - 1]?.quarterColumn.amount() || 0,
        })[comparison]?.();
      },
      value: _ => {
        return compute(quarterColumn.amount(), quarterColumn.comparisonAmount());
      },
    };
    return {
      isCurrent,
      monthColumns,
      quarterColumn,
    };
  });
  return allQuarters;
};

const generateRowGroups = (relatedCompanies, yearMonths, monthItems, prevYearMonthItems, documentType, comparison = 'prevYear', fiscalYears = [], prevYearMonths = [], prevYearLeadMonthItem = null, allConsolidationJournalTypes = []) => {
  const accountItemCategories = accountItemCategoriesGroupedByType[documentType];
  const items = unzip([...prevYearMonthItems, ...monthItems]).map((allMonthItemColumns) => {
    const { itemType, accountItemCategory } = allMonthItemColumns[0];
    return {
      itemType,
      accountItemCategory,
      allMonthItemColumns,
    }
  });
  const { consolidationAccountItem: nonCategoryItems = [], category: categoryItems = [], } = groupBy(items, 'itemType');
  const nonCategoryItemsGroupedByCategory = groupBy(nonCategoryItems, 'accountItemCategory.name');
  const categoryItemsByCategory = keyBy(categoryItems, 'accountItemCategory.name');
  // NOTE: 前期先頭月の暦月前月(リード月)のアイテムを key で引けるようにする(単月化用)。
  const prevYearLeadMonthItemByKey = keyBy(prevYearLeadMonthItem || [], 'key');
  const rowGroups = accountItemCategories.map((category) => {
    const directItems = nonCategoryItemsGroupedByCategory[category.name] || [];
    const categoryItem = categoryItemsByCategory[category.name];
    return {
      category,
      itemRowGroups: [...directItems, categoryItem].map(({ allMonthItemColumns }) => {
        const { key, item, } = allMonthItemColumns[0];
        const currentMonthItemColumns = allMonthItemColumns.slice(prevYearMonthItems.length);
        const relatedCompanyRowGroups = relatedCompanies.map((relatedCompany, relatedCompanyIndex) => {
          const buildAggregationTypeRowGroup = ({ aggregationType, consolidationJournalTypeId = null, amountOfColumn, }) => {
            const amountOf = (itemColumn) => (itemColumn == null ? 0 : amountOfColumn(itemColumn.companyColumns[relatedCompanyIndex])) || 0;
            // NOTE: 金額は数値種別(metrics)に依存しないので、種別ごとに作り直さず先に1度だけ算出する。
            const baseMonthColumns = allMonthItemColumns.map((monthItemColumn, monthIndex) => {
              const isCurrent = monthIndex > prevYearMonthItems.length - 1;
              const isCurrentFirst = isCurrent && monthIndex === prevYearMonthItems.length;
              const comparisonMonthIndex = monthIndex - ({ prevYear: 12, prevMonth: 1})[comparison];
              const comparisonMonthItemColumn = allMonthItemColumns[comparisonMonthIndex];
              const amount = amountOfColumn(monthItemColumn.companyColumns[relatedCompanyIndex]);
              const prevMonthAmount = amountOf(allMonthItemColumns[monthIndex - 1]);
              const displayAmount = documentType === 'bs' ? amount : amount - (isCurrentFirst ? 0 : prevMonthAmount);
              // NOTE: 前期(prevYear)の単月化。決算期変更で前期の決算期期首が当期とずれるため、
              // 「前期対象月が自分の決算期の期首か」を fiscalYears で判定し、期首なら前月を引かない。
              // 前期配列の先頭月の暦月前月は配列に無いので、別途取得した prevYearLeadMonthItem を使う。
              const comparisonYearMonth = comparison === 'prevYear' ? prevYearMonths[comparisonMonthIndex] : null;
              const isComparisonFirst = comparison === 'prevYear'
                ? (comparisonYearMonth != null && isFiscalYearFirstMonth(comparisonYearMonth, fiscalYears))
                : (isCurrent && comparisonMonthIndex === prevYearMonthItems.length);
              const comparisonAmount = amountOf(comparisonMonthItemColumn);
              const comparisonPrevMonthItemColumn = (comparison === 'prevYear' && comparisonMonthIndex === 0)
                ? prevYearLeadMonthItemByKey[key]
                : allMonthItemColumns[comparisonMonthIndex - 1];
              const comparisonPrevMonthAmount = amountOf(comparisonPrevMonthItemColumn);
              const comparisonDisplayAmount = documentType === 'bs' ? comparisonAmount : comparisonAmount - (isComparisonFirst ? 0 : comparisonPrevMonthAmount);
              return {
                isCurrent,
                isCurrentFirst,
                amount,
                prevMonthAmount,
                displayAmount,
                comparisonAmount,
                comparisonPrevMonthAmount,
                comparisonDisplayAmount,
              };
            });
            const metricRows = entries(metrics).map(([metric, { compute }]) => {
              const allMonthColumns = baseMonthColumns.map((baseMonthColumn) => {
                const { displayAmount, comparisonDisplayAmount } = baseMonthColumn;
                return {
                  ...baseMonthColumn,
                  value: _ => compute(displayAmount, comparisonDisplayAmount),
                };
              });
              const monthColumns = allMonthColumns.filter(_ => _.isCurrent);
              const allQuarters = generateQuarters(documentType, comparison, allMonthColumns, compute);
              const quarters = allQuarters.filter(_ => _.isCurrent);
              return {
                metric,
                monthColumns,
                allMonthColumns,
                quarters,
                isAllNone: monthColumns.every(_ => !(Math.abs(_.value()) > 0)),
              };
            });
            const sumAmount = sumBy(metricRows[0].monthColumns, _ => _.value());
            const comparisonSumAmount = sumBy(metricRows[1].monthColumns, _ => _.value())
            const summaryComputedMetricRows = metricRows.map((metricRow) => {
              const { metric } = metricRow;
              const shouldNaN = metric !== 'current' && comparison === 'prevMonth';
              const summaryColumns = [
                {
                  type: 'total',
                  value: _ => shouldNaN ? NaN : metrics[metric].compute(sumAmount, comparisonSumAmount),
                },
              ];
              return {
                ...metricRow,
                summaryColumns,
              };
            });
            return {
              aggregationType,
              consolidationJournalTypeId,
              metricRows: summaryComputedMetricRows,
              isAllNone: metricRows.every(_ => _.isAllNone),
            };
          };
          // NOTE: 仕訳種別の行は (科目 x 会社 x 種別) の組み合わせ数だけ作られるが、実データでは
          // 大半が全月ゼロになる。全月ゼロなら派生する金額も必ずゼロなので、1つ作った結果を
          // 使い回して組み立てコストを省く(出力される値は都度作った場合と同じ)。
          let zeroConsolidationJournalTypeRowGroup = null;
          const buildConsolidationJournalTypeRowGroup = (id) => {
            const amountOfColumn = _ => _?.consolidationAmountsByType?.[id];
            const isAllZero = allMonthItemColumns.every(_ => !amountOfColumn(_.companyColumns[relatedCompanyIndex]));
            if(!isAllZero) return buildAggregationTypeRowGroup({ aggregationType: 'consolidation', consolidationJournalTypeId: id, amountOfColumn, });

            zeroConsolidationJournalTypeRowGroup = zeroConsolidationJournalTypeRowGroup || buildAggregationTypeRowGroup({ aggregationType: 'consolidation', amountOfColumn: _ => 0, });
            return { ...zeroConsolidationJournalTypeRowGroup, consolidationJournalTypeId: id };
          };
          const aggregationTypeRowGroups = entries(aggregationTypes).map(([aggregationType]) => {
            const { amountName } = aggregationTypes[aggregationType];
            const aggregationTypeRowGroup = buildAggregationTypeRowGroup({ aggregationType, amountOfColumn: _ => _?.[amountName], });
            return aggregationType === 'consolidation' ? {
              ...aggregationTypeRowGroup,
              consolidationJournalTypeRowGroups: allConsolidationJournalTypes.map(_ => buildConsolidationJournalTypeRowGroup(_.id)),
            } : aggregationTypeRowGroup;
          });
          return {
            relatedCompany,
            aggregationTypeRowGroups,
            isAllNone: aggregationTypeRowGroups.every(_ => _.isAllNone),
          };
        });
        return {
          key,
          item,
          relatedCompanyRowGroups,
          isAllNone: relatedCompanyRowGroups.every(_ => _.isAllNone),
        };
      })
        .filter(_ => !_.isAllNone),
    };
  });
  return rowGroups;
};

const computeItemSummaryRowGroup = (documentType, comparison, yearMonths, prevYearMonths, relatedCompanyRowGroups) => {
  const allMonthConsolidatedAmounts = [...prevYearMonths, ...yearMonths].map((yearMonth, yearMonthIndex) => {
    return {
      ...['amount', 'prevMonthAmount', 'displayAmount', 'comparisonAmount', 'comparisonPrevMonthAmount', 'comparisonDisplayAmount'].reduce((x, y) => {
        return {
          ...x,
          [y]: sumBy(relatedCompanyRowGroups.map(_ => last(_.aggregationTypeRowGroups).metricRows[0].allMonthColumns[yearMonthIndex]), y),
        };
      }, {}),
    };
  });
  const itemSummaryRowGroup = {
    relatedCompany: null,
    aggregationTypeRowGroups: [
      (() => {
        const metricRows = entries(metrics).map(([metric, { compute }], metricIndex) => {
          const allMonthColumns = [...prevYearMonths, ...yearMonths].map((yearMonth, yearMonthIndex) => {
            const isCurrent = yearMonthIndex > prevYearMonths.length - 1;
            const isCurrentFirst = isCurrent && yearMonthIndex === prevYearMonths.length;
            const { amount, comparisonAmount, displayAmount, comparisonDisplayAmount, } = allMonthConsolidatedAmounts[yearMonthIndex];
            return {
              isCurrent,
              isCurrentFirst,
              amount,
              comparisonAmount,
              displayAmount,
              comparisonDisplayAmount,
              value: _ => compute(displayAmount, comparisonDisplayAmount),
            };
          });
          const monthColumns = allMonthColumns.filter(_ => _.isCurrent);
          const allQuarters = generateQuarters(documentType, comparison, allMonthColumns, metrics[metric].compute);
          const quarters = allQuarters.filter(_ => _.isCurrent);
          return {
            metric,
            monthColumns,
            quarters,
          };
        });
        const sumAmount = sumBy(metricRows[0].monthColumns, _ => _.value());
        const comparisonSumAmount = sumBy(metricRows[1].monthColumns, _ => _.value());
        const summaryComputedMetricRows = metricRows.map((metricRow) => {
          const { metric } = metricRow;
          const shouldNaN = metric !== 'current' && comparison === 'prevMonth';
          const summaryColumns = [
            {
              type: 'total',
              value: _ => shouldNaN ? NaN : metrics[metric].compute(sumAmount, comparisonSumAmount),
            },
          ];
          return {
            ...metricRow,
            summaryColumns,
          };
        });
        return {
          aggregationType: 'consolidated',
          metricRows: summaryComputedMetricRows,
        };
      })()
    ],
  };
  return itemSummaryRowGroup;
};

const sumAggregationTypeRowGroups = (aggregationTypeRowGroups, aggregationType = 'consolidation') => {
  const [first] = aggregationTypeRowGroups;
  if(first == null) return null;

  const sumOfColumns = (columns) => ({
    ...columns[0],
    value: _ => sumBy(columns, _ => _.value() || 0),
  });
  const metricIndexByMetric = fromPairs(first.metricRows.map((_, i) => [_.metric, i]));
  const sumValueAt = (metric, pick) => {
    const metricRows = aggregationTypeRowGroups.map(_ => _.metricRows[metricIndexByMetric[metric]]);
    return sumBy(metricRows, _ => pick(_)?.value() || 0);
  };
  const metricRows = first.metricRows.map((metricRow, metricIndex) => {
    const sameMetricRows = aggregationTypeRowGroups.map(_ => _.metricRows[metricIndex]);
    if(metricRow.metric === 'changeRate') {
      const rateOfColumn = (pick) => ({
        ...pick(metricRow),
        value: _ => metrics.changeRate.compute(sumValueAt('current', pick), sumValueAt('prev', pick)),
      });
      return {
        ...metricRow,
        monthColumns: metricRow.monthColumns.map((_, i) => rateOfColumn(_ => _.monthColumns[i])),
        allMonthColumns: metricRow.allMonthColumns.map((_, i) => rateOfColumn(_ => _.allMonthColumns[i])),
        quarters: metricRow.quarters.map((quarter, quarterIndex) => ({
          ...quarter,
          monthColumns: quarter.monthColumns.map((_, i) => rateOfColumn(_ => _.quarters[quarterIndex].monthColumns[i])),
          quarterColumn: rateOfColumn(_ => _.quarters[quarterIndex].quarterColumn),
        })),
        summaryColumns: metricRow.summaryColumns.map((_, i) => rateOfColumn(_ => _.summaryColumns[i])),
        isAllNone: sameMetricRows.every(_ => _.isAllNone),
      };
    }
    return {
      ...metricRow,
      monthColumns: metricRow.monthColumns.map((monthColumn, i) => sumOfColumns(sameMetricRows.map(_ => _.monthColumns[i]))),
      allMonthColumns: metricRow.allMonthColumns.map((monthColumn, i) => sumOfColumns(sameMetricRows.map(_ => _.allMonthColumns[i]))),
      quarters: metricRow.quarters.map((quarter, quarterIndex) => ({
        ...quarter,
        monthColumns: quarter.monthColumns.map((monthColumn, i) => sumOfColumns(sameMetricRows.map(_ => _.quarters[quarterIndex].monthColumns[i]))),
        quarterColumn: sumOfColumns(sameMetricRows.map(_ => _.quarters[quarterIndex].quarterColumn)),
      })),
      summaryColumns: metricRow.summaryColumns.map((summaryColumn, i) => sumOfColumns(sameMetricRows.map(_ => _.summaryColumns[i]))),
      isAllNone: sameMetricRows.every(_ => _.isAllNone),
    };
  });
  return {
    ...first,
    aggregationType,
    consolidationJournalTypeId: null,
    metricRows,
    isAllNone: aggregationTypeRowGroups.every(_ => _.isAllNone),
  };
};

const displayColumnTypes = {
  month: { label: '月', },
  quarter: { label: '四半期', },
};

const comparisons = {
  prevYear: { label: '前期', },
  prevMonth: { label: '前月(前四半期)', },
};

exports.aggregationTypes = aggregationTypes;
exports.metrics = metrics;
exports.generateRowGroups = generateRowGroups;
exports.computeItemSummaryRowGroup = computeItemSummaryRowGroup;
exports.sumAggregationTypeRowGroups = sumAggregationTypeRowGroups;
exports.displayColumnTypes = displayColumnTypes;
exports.comparisons = comparisons;
